import { Link } from "wouter";
import { Phone, Home, Paintbrush, ArrowLeft, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { trackPhoneCall } from "@/lib/googleAds";
import { useEffect } from "react";

const SERVICES = [
  { label: "Interior & Exterior Painting", href: "/service/interior-exterior-painting" },
  { label: "Commercial Painting", href: "/service/commercial-painting" },
  { label: "Cabinet Painting", href: "/service/cabinet-painting" },
  { label: "Roof Coating & Repair", href: "/service/roof-coating-repair" },
  { label: "Waterproofing", href: "/service/waterproofing" },
  { label: "Pressure Washing", href: "/service/pressure-washing" },
  { label: "Drywall Repair", href: "/service/drywall-repair" },
  { label: "Deck & Staircase Repair", href: "/service/deck-staircase-repair" },
  { label: "Trim & Molding", href: "/service/trim-molding" },
  { label: "Exterior Painting", href: "/service/exterior-painting" },
];

const CITIES = [
  { label: "Santa Barbara", href: "/city/santa-barbara" },
  { label: "Ventura", href: "/city/ventura" },
  { label: "Montecito", href: "/city/montecito" },
  { label: "Goleta", href: "/city/goleta" },
  { label: "Carpinteria", href: "/city/carpinteria" },
  { label: "Ojai", href: "/city/ojai" },
  { label: "Camarillo", href: "/city/camarillo" },
  { label: "Solvang", href: "/city/solvang" },
  { label: "Buellton", href: "/city/buellton" },
  { label: "Santa Ynez", href: "/city/santa-ynez" },
  { label: "Isla Vista", href: "/city/isla-vista" },
  { label: "Summerland", href: "/city/summerland" },
  { label: "Oxnard", href: "/city/oxnard" },
  { label: "Los Angeles", href: "/city/los-angeles" },
];

export default function NotFound() {
  useEffect(() => {
    document.title = "Page Not Found | Danis Painting Co.";
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section
        className="relative pt-32 pb-16 text-white overflow-hidden"
        style={{ background: "linear-gradient(135deg, #0a1628 0%, #1e3a5f 50%, #0a1628 100%)" }}
      >
        <div className="container px-4 text-center">
          <p className="text-7xl font-black text-white/20 mb-2 select-none">404</p>
          <h1 className="text-3xl md:text-4xl font-bold mb-4">Page Not Found</h1>
          <p className="text-white/80 text-lg max-w-xl mx-auto mb-8">
            Sorry, the page you're looking for doesn't exist or has been moved. Let us help you find what you need.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:805-403-8727"
              onClick={trackPhoneCall}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#4caf30] text-white font-semibold rounded-lg hover:bg-[#3d9426] transition-colors"
            >
              <Phone className="w-5 h-5" />
              Call: 805-403-8727
            </a>
            <Link href="/">
              <button className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-white/40 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
                <Home className="w-5 h-5" />
                Go to Homepage
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-12 bg-gray-50">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto">
            {/* Services */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Paintbrush className="w-5 h-5 text-[#4caf30]" />
                Our Services
              </h2>
              <ul className="space-y-2">
                {SERVICES.map(s => (
                  <li key={s.href}>
                    <Link href={s.href} className="text-[#4caf30] hover:text-[#3d9426] hover:underline text-sm">
                      {s.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Cities */}
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-[#4caf30]" />
                Service Areas
              </h2>
              <ul className="space-y-2">
                {CITIES.map(c => (
                  <li key={c.href}>
                    <Link href={c.href} className="text-[#4caf30] hover:text-[#3d9426] hover:underline text-sm">
                      {c.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Back link */}
          <div className="text-center mt-10">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Go back to previous page
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
